/*
  mosy placement tracking.

  Any element carrying data-placement="<mosy placement id>" (see
  snippets/placement.attrs.liquid) gets two data-layer events:
    - placement_view  : the element has been on screen for one second, with
                        at least half of it visible, or filling at least half
                        of the viewport when it is taller than the viewport.
                        Once per placement per page load.
    - placement_click : a click anywhere inside the element.
  Both carry placement_id, placement_type, placement_brand, page_type and
  page_path. The Stape app forwards the data layer to the server container,
  which fans out to GA4 and BigQuery. No personal data is added here.
  Skipped inside the Shopify theme editor.
*/
(() => {
  if (window.Shopify && window.Shopify.designMode) return;
  if (!('IntersectionObserver' in window)) return;

  const script = document.currentScript;
  const pageType = (script && script.dataset.page) || 'other';
  const viewed = new Set();
  const timers = new WeakMap();

  const payload = (event, el, extra) =>
    Object.assign(
      {
        event,
        placement_id: el.dataset.placement,
        placement_type: el.dataset.placementType || '',
        placement_brand: el.dataset.placementBrand || '',
        page_type: pageType,
        page_path: window.location.pathname,
      },
      extra || {}
    );

  const push = (event, el, extra) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload(event, el, extra));
  };

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const el = entry.target;
        const id = el.dataset.placement;
        if (viewed.has(id)) {
          io.unobserve(el);
          return;
        }
        const viewportHalf = (window.innerHeight || 0) * 0.5;
        const visibleEnough =
          entry.intersectionRatio >= 0.5 ||
          entry.intersectionRect.height >= viewportHalf;
        if (entry.isIntersecting && visibleEnough) {
          if (!timers.has(el)) {
            timers.set(
              el,
              window.setTimeout(() => {
                timers.delete(el);
                if (viewed.has(id)) return;
                viewed.add(id);
                io.unobserve(el);
                push('placement_view', el);
              }, 1000)
            );
          }
        } else if (timers.has(el)) {
          window.clearTimeout(timers.get(el));
          timers.delete(el);
        }
      });
    },
    { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1] }
  );

  const observe = () => {
    document.querySelectorAll('[data-placement]').forEach((el) => {
      if (el.dataset.placementObserved) return;
      el.dataset.placementObserved = '1';
      if (!viewed.has(el.dataset.placement)) io.observe(el);
    });
  };

  observe();
  new MutationObserver(observe).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  // Capture phase so the event is queued before a link navigates away.
  document.addEventListener(
    'click',
    (ev) => {
      const target = ev.target instanceof Element ? ev.target : null;
      const el = target && target.closest('[data-placement]');
      if (!el) return;
      const link = target.closest('a[href]');
      push('placement_click', el, { link_url: link ? link.href : '' });
    },
    true
  );
})();
